import {
  SimpleTreeItemWrapper,
  TreeItemComponentProps,
} from 'dnd-kit-sortable-tree';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import DragDropDots from 'assets/icons/drag-drop-dots.svg?react';
import DropdownIcon from 'assets/icons/arrow-down-grey.svg?react';
import Dots from 'assets/icons/dots.svg?react';
import styles from './TemplateListItem.module.scss';
import clsx from 'clsx';
import { Loading } from 'components/uikit/suspense/Loading';
import { Tooltip } from '@mui/material';
import { AppMenu, AppMenuItem } from 'components/uikit/menu/AppMenu';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { AppLink } from 'components/uikit/buttons/AppLink';
import { QueryFactory } from 'services/api';
import { useQueryClient } from '@tanstack/react-query';
import { Links } from 'application/constants/links';
import {
  isTemplate,
  TemplateTreeNode,
} from 'helpers/documents/types/templates';
import {
  FolderDeletionBlockReason,
  isFolder,
} from 'helpers/documents/types/base';
import {
  createProjectDocument,
  createTemplates,
  DocumentData,
  downloadTemplate,
  selectFiles,
} from 'helpers/documents/fileHandler';
import { createTemplateFolderNode } from 'helpers/documents/documentsObserver';
import { AddFolderModal } from 'pages/authorized/documents/documentList/addFolder/AddFolderModal';
import { EditItemModal } from 'pages/authorized/documents/documentList/editItem/EditItemModal';
import { DeleteItemModal } from 'pages/authorized/documents/documentList/deleteFolder/DeleteItemModal';
import { convertDateToString } from 'helpers/date-helpers';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { ImportIntoProjectModal } from './ImportIntoProjectModal/ImportIntoProjectModal';
import { VersionHistoryModal } from './VersionHistoryModal/VersionHistoryModal';
import { UploadNewVersionModal } from './UploadNewVersionModal/UploadNewVersionModal';
import { useGridTemplate } from 'helpers/useGridTemplate';
import {
  DocumentDeletionBlockReason,
  Status,
} from 'services/api/api-client.types';

export const TemplateListItem = forwardRef<
  HTMLDivElement,
  TreeItemComponentProps<TemplateTreeNode>
>((props, ref) => {
  const { collapsed, item, parent } = props;

  const hasChildren = item.children && item.children.length > 0;
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('Page.Templates.TemplateListItem');
  const deleteModalTitle = useScopedTranslation('Component.DeleteItemModal');
  const i18nMenu = useScopedTranslation('Page.Templates.menuItems');
  const i18nDeleteReasons = useScopedTranslation(
    'Page.Templates.DeleteDisableReasons',
  );
  const editModalTitle = useScopedTranslation('Component.EditFolderModal');
  const uploadNewVersionModalTitle = useScopedTranslation(
    'Component.UploadNewVersionModal',
  );
  const enumsI18n = useScopedTranslation('Enums');

  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isUploadNewVersionModalOpen, setIsUploadNewVersionModalOpen] =
    useState(false);
  const [folderCollapsed, setFolderCollapsed] = useState(
    hasChildren ? collapsed : true,
  );
  const menuAnchorRef = useRef<HTMLElement>(null);
  const onShowContextualMenu = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.stopPropagation();
      ev.preventDefault();
      setShowContextualMenu(true);
    },
    [],
  );

  const deleteDisabled =
    hasChildren ||
    !item.canBeDeleted ||
    (!!item.deletionBlockReason &&
      item.deletionBlockReason !== DocumentDeletionBlockReason.None);
  const _isTemplate = isTemplate(item);
  const _isFolder = isFolder(item);
  const expanded = !folderCollapsed && !_isTemplate;
  let itemName;

  const onItemDelete = async () => {
    await item.onDelete?.(item.id);
  };

  const invalidateWithDependencies = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.getFoldersQueryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.searchQueryKey(),
      }),
    ]);
  };

  const onFolderEdit = async (editedName: string) => {
    if (_isFolder) {
      await item.onFolderEdit(item.id, editedName, 'name');
    }
  };

  const getDeleteTooltip = (
    hasChildren: boolean | undefined,
    deletionBlockReason:
      | DocumentDeletionBlockReason
      | FolderDeletionBlockReason
      | undefined,
  ) => {
    if (hasChildren) return i18n.t('delete_not_empty_folder_message');

    switch (deletionBlockReason) {
      case DocumentDeletionBlockReason.HasDerivedDocuments:
        return i18nDeleteReasons.t('hasDerivedDocuments');
      default:
        return undefined;
    }
  };

  const menuItems = useMemo(() => {
    const items: AppMenuItem[] = [
      {
        key: 'delete',
        text: i18nMenu.t('delete'),
        className: styles.delete,
        disabled: deleteDisabled,
        tooltipMessage: getDeleteTooltip(hasChildren, item.deletionBlockReason),
        onClick: () => {
          setShowContextualMenu(false);
          setIsDeleteModalOpen(true);
        },
      },
    ];

    if (_isTemplate) {
      items.unshift(
        {
          key: 'download-template',
          text: i18n.t('MenuItems.download'),
          className: styles.menuItem,
          onClick: async () => {
            setIsLoading(true);
            await downloadTemplate(item.documentId);
            setIsLoading(false);
          },
        },
        {
          key: 'import-into-project',
          text: i18n.t('MenuItems.import_into_project'),
          className: styles.separatorMenuItem,
          onClick: async () => {
            setShowContextualMenu(false);
            setIsImportModalOpen(true);
          },
        },
        {
          key: 'view-version-history',
          text: i18n.t('MenuItems.view_version_history'),
          className: styles.menuItem,
          onClick: async () => {
            setShowContextualMenu(false);
            setIsVersionHistoryOpen(true);
          },
        },
        {
          key: 'upload-version',
          text: i18n.t('MenuItems.upload_version'),
          className: styles.separatorMenuItem,
          onClick: async () => {
            setShowContextualMenu(false);
            setIsUploadNewVersionModalOpen(true);
          },
        },
      );
    }

    if (_isFolder) {
      items.unshift(
        {
          key: 'upload-template',
          text: i18nMenu.t('upload_template'),
          className: styles.menuItem,
          onClick: async () => {
            const files = await selectFiles(true, true);
            setIsLoading(true);
            setShowContextualMenu(false);
            try {
              await createTemplates(files, invalidateWithDependencies, item.id);
            } finally {
              setIsLoading(false);
            }
          },
        },
        {
          key: 'folder',
          text: i18nMenu.t('new_folder'),
          className: styles.separatorMenuItem,
          onClick: async () => {
            setIsAddModalOpen(true);
          },
        },
        {
          key: 'edit',
          text: i18nMenu.t('edit'),
          className: styles.menuItem,
          onClick: () => {
            setShowContextualMenu(false);
            setIsEditModalOpen(true);
          },
        },
      );
    }

    return items;
  }, [hasChildren, deleteDisabled, _isFolder, _isTemplate && item.documentId]);

  if (_isTemplate) {
    itemName = (
      <AppLink
        testId="list-item-name"
        to={Links.Authorized.OpenTemplate.link({
          templateId: item.documentId,
        })}
        className={styles.link}
      >
        {item.name}
      </AppLink>
    );
  } else {
    itemName = (
      <div data-test-id="list-item-name" className={styles.folderName}>
        {item.name}
      </div>
    );
  }

  const { hiddenColumns, grid } = useGridTemplate(
    'template', // table id
    'TemplateListItem', // grid type
  );

  return (
    <div
      data-test-id="list-item"
      className={styles.mainContainer}
      onClick={() => {
        setFolderCollapsed(!folderCollapsed);
      }}
    >
      <Loading
        loading={isLoading}
        className={styles.loader}
        wrapperClassName={styles.loaderWrapper}
      >
        <SimpleTreeItemWrapper
          {...props}
          showDragHandle={false}
          manualDrag={true}
          ref={ref}
          key={item.id}
          indentationWidth={28}
        >
          <div
            className={clsx(
              styles.buttonsContainer,
              expanded ? styles.folderExpanded : styles.folderCollapsed,
            )}
          >
            <a
              className={clsx(
                expanded
                  ? styles.dragHandleExpanded
                  : styles.dragHandleCollapsed,
                item.disableSorting && styles.dragDropNone,
              )}
              {...props.handleProps}
            >
              <DragDropDots />
            </a>

            <DropdownIcon
              className={clsx(
                _isTemplate
                  ? styles.folderArrowHidden
                  : expanded
                  ? styles.folderArrowExpanded
                  : styles.folderArrowCollapsed,
              )}
            />
          </div>

          <div
            className={clsx(
              styles.wrapper,
              !_isTemplate && styles.folderWrapper,
            )}
          >
            <div
              className={clsx(
                folderCollapsed || _isTemplate
                  ? styles.treeItem
                  : styles.treeItemOpened,
              )}
              style={{ gridTemplateColumns: grid }}
            >
              <Tooltip title={item.name} placement={'top-start'}>
                <div className={styles.treeItemName}>{itemName}</div>
              </Tooltip>

              {_isTemplate && item.activity && (
                <div
                  data-test-id="activity"
                  className={clsx(styles.activity, styles.column, {
                    [styles.columnHidden]: hiddenColumns.includes('activity'),
                  })}
                >
                  <span className={styles.activityName}>
                    {item.activity.user + ' '}
                  </span>
                  <span className={styles.activityDate}>
                    {i18n.t(`ActivityStatuses.${item.activity.action}`) + ' '}
                    {convertDateToString({
                      date: item.activity.date,
                      divider: '.',
                      mirrorOrder: true,
                    })}
                  </span>
                </div>
              )}
            </div>
            <div
              className={styles.dotMenuImage}
              ref={menuAnchorRef as any}
              onClick={onShowContextualMenu}
              data-test-id="dot-menu"
            >
              <Dots />
            </div>
            <AppMenu
              className={styles.contextualMenu}
              anchorEl={menuAnchorRef.current}
              open={showContextualMenu}
              onClose={() => {
                setShowContextualMenu(false);
              }}
              menuItems={menuItems}
              anchorOrigin={{ horizontal: 26, vertical: 'bottom' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            />
          </div>
        </SimpleTreeItemWrapper>
      </Loading>
      <AddFolderModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        createFolderNode={async (name, id) => {
          const folderNode = createTemplateFolderNode(id, name);
          if (_isFolder) {
            setIsAddModalOpen(true);
            await item.onAddSubItem(item.id, folderNode);
          }
          setShowContextualMenu(false);
        }}
      />
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isBlocking={true}
        title={
          _isFolder
            ? deleteModalTitle.t('title_folder')
            : deleteModalTitle.t('title_template')
        }
      >
        <DeleteItemModal
          item={item}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={async () => await onItemDelete()}
        />
      </CustomModal>
      <CustomModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
        }}
        isBlocking={true}
        title={editModalTitle.t('edit_title', {
          itemType: enumsI18n.t(`NodeType.${item.type}`).toLowerCase(),
        })}
      >
        <EditItemModal
          id={item.id}
          name={item.name}
          folders={parent ? parent.children! : [item]}
          onClose={() => setIsEditModalOpen(false)}
          onFolderSubmit={(name) => onFolderEdit(name)}
          onFileSubmit={() => {}}
        />
      </CustomModal>

      {_isTemplate && (
        <>
          <ImportIntoProjectModal
            onImport={async (latestRevision) => {
              const templateId = item.documentId;
              const folders = latestRevision.folders;

              const document =
                await QueryFactory.DocumentClient.deferCreateFromTemplate({
                  templateId: templateId,
                  revisionId: latestRevision.id,
                  folderId: null,
                  status: Status.Draft,
                });

              const documentNode = createProjectDocument(
                String(document.id),
                document.name,
                document.id,
              );

              folders.push(documentNode);

              await QueryFactory.RevisionClient.patch(latestRevision.id, {
                folders,
              });

              await queryClient.invalidateQueries({
                queryKey: QueryFactory.RevisionQuery.getQueryKey(
                  latestRevision.id,
                ),
              });
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
              });
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.TemplateQuery.searchQueryKey(),
              });
            }}
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            importingNames={[props.item.name]}
          />
          <CustomModal
            isOpen={isUploadNewVersionModalOpen}
            onClose={() => {
              setIsUploadNewVersionModalOpen(false);
            }}
            isBlocking={true}
            title={uploadNewVersionModalTitle.t('title')}
          >
            <UploadNewVersionModal
              templateName={item.name}
              isOpen={isUploadNewVersionModalOpen}
              onCreate={async (template: DocumentData, comment: string) => {
                try {
                  await createTemplates(
                    [template],
                    invalidateWithDependencies,
                    parent?.id,
                    Number(item.id),
                    comment.length === 0 ? undefined : comment,
                  );
                } catch {}
              }}
              onClose={() => setIsUploadNewVersionModalOpen(false)}
            />
          </CustomModal>
          <VersionHistoryModal
            isOpen={isVersionHistoryOpen}
            onClose={() => setIsVersionHistoryOpen(false)}
            templateId={Number(item.id)}
          />
        </>
      )}
    </div>
  );
});
